// @ts-strict-ignore
import { LIQUID_REGEXES } from "@lib/constants/liquidTemplateConstants";
import templatableUtil from "@lib/liquid/templatableUtil";
import sharedConstants from "@lib/shared_constants";

// NOTE: A lot of logic in this module is also contained in back-end code in
// shared_code/domains/link_shortening/public/appboy/communication/braze_link_shortener/link_shortener_helper.rb
//
// If you are making a change here it is also worth considering if a corresponding change needs to be
// made to the back-end code.
export const PLACEHOLDER_SHORT_URL_USER_ID_PATH = "/YYYYYY";
export const URL_ID_LENGTH = PLACEHOLDER_SHORT_URL_USER_ID_PATH.length;

const VALID_HTTP_URL_REGEXP = new RegExp(sharedConstants.SMS.VALID_HTTP_URL_REGEX);

// Tokenizes a string using whitespace as the word boundary. Whitespace within
// liquid templates will be ignored.  Whitespace that is not within liquid templates
// will be included in returned tokens.
//
// e.g. "{{${first_name} | default: 'Bob'}} is\n\t  cool" would be tokenized as
// [ "{{${first_name} | default: 'Bob'}}", ' ', 'is', '\n\t  ', 'cool' ]
export const tokenizeOnNonLiquidWhitespace = (s: string) => {
  if (!s) {
    return [];
  }

  // Find all liquid templates within string
  const originalLiquidMatches = s.match(LIQUID_REGEXES.TAG_OR_OBJECT) || [];

  // Because liquid templates can contain whitespace, replace all liquid templates with placeholder,
  // maintaining map of placeholder -> template to reconstruct original string after tokenization
  const liquidPlaceholderToOriginal = {};
  let stringWithLiquidPlaceholders = s;
  let liquidPlaceholderIndex = 1;
  for (const original of originalLiquidMatches) {
    const liquidPlaceholder = `BRAZE-LIQUID-${liquidPlaceholderIndex}-PLACEHOLDER`;
    liquidPlaceholderToOriginal[liquidPlaceholder] = original;
    stringWithLiquidPlaceholders = stringWithLiquidPlaceholders.replace(original, liquidPlaceholder);
    liquidPlaceholderIndex += 1;
  }

  // Now that string has had liquid templates replaced with placeholders, tokenize string on whitespace
  // using capturing group to make sure whitespace is returned as tokens.
  // Replace placeholders in tokens with original liquid templates to return.
  const liquidPlaceholderRegex = new RegExp(/BRAZE-LIQUID-\d+-PLACEHOLDER/, "g");
  return stringWithLiquidPlaceholders.split(/(\s+)/).map((substring) => {
    // A token can have multiple liquid placeholders, e.g "https://www.example.com{% if foo %}?bar=true{% endif %}"
    const placeholderMatches = substring.match(liquidPlaceholderRegex) || [];
    let tokenText = substring;
    if (placeholderMatches) {
      for (const liquidPlaceholder of placeholderMatches) {
        tokenText = tokenText.replace(liquidPlaceholder, liquidPlaceholderToOriginal[liquidPlaceholder]);
      }
    }

    return tokenText;
  });
};

export const isValidHttpUrl = (s: string) => {
  try {
    // Use URL constructor to attempt to parse string as URL.
    return new URL(s) && VALID_HTTP_URL_REGEXP.test(s);
  } catch (_) {
    return false;
  }
};

export const isValidPersonalizedHttpUrl = (url: string) => {
  // Ensure liquid is present and remove any liquid templates and validate URL
  return templatableUtil.hasLiquid(url) && isValidHttpUrl(url.replace(LIQUID_REGEXES.TAG_OR_OBJECT, ""));
};

export const isPersonalizedHttpUrl = (url: string) => {
  // Ensure liquid is present and remove any liquid templates and validate URL
  return (
    isValidPersonalizedHttpUrl(url) ||
    (templatableUtil.hasLiquid(url) && (url.slice(0, 5) === "http:" || url.slice(0, 6) === "https:"))
  );
};

// Hostnames are case-insensitive per RFC 3986 and a bare http(s) host (https://foo) is a valid
// URL that must still resolve to its own placeholder rather than collapsing onto the primary.
// Matches both schemes so this stays in sync with extractHttpUrl which accepts http and https.
export const urlMatchesHost = (url: string, host: string): boolean => {
  const match = url.match(/^https?:\/\/([^/?#:]+)/i);
  return match !== null && match[1].toLowerCase() === host.toLowerCase();
};

export const extractHttpUrl = (token: string): string | null => {
  const urlStartMatch = token.match(/https?:\/\//i);
  if (!urlStartMatch || urlStartMatch.index === undefined) {
    return null;
  }

  const urlStartIndex = urlStartMatch.index;
  return token.substring(urlStartIndex);
};

// True when a tokenized chunk is a link — either a literal http(s) URL or a personalized
// (Liquid) URL that resolves to one. This is the canonical "is this token a link" test used
// across the SMS/RCS composer (preview link highlighting, length stats, etc.).
export const isLinkToken = (token: string): boolean => {
  return isValidHttpUrl(token) || isPersonalizedHttpUrl(token);
};

// True when a message body contains at least one link (literal or personalized).
export const textHasLink = (text: string): boolean => {
  return tokenizeOnNonLiquidWhitespace(text).some(isLinkToken);
};

// Substitutes eligible, valid URLs within passed string with placeholderShortUrl
// Ineligible URLs that will NOT be replaced include:
// - Personalized URLs (URLs composed with liquid templates e.g https://example.com/{{attribute}})
// - URLs that are within connected content liquid template (e.g {% connected_content https://example.com %})
export const substituteUrlsWithPlaceholderShortUrls = (
  s: string,
  hostname: string,
  userClickTrackingEnabled: boolean,
  personalizeAllUrls: boolean,
  useUnifiedShortLinkFormat = false,
  additionalHostnames: readonly string[] = [],
  restrictToRegisteredHosts = false
) => {
  return tokenizeWithPlaceholderShortUrls(
    s,
    hostname,
    userClickTrackingEnabled,
    personalizeAllUrls,
    useUnifiedShortLinkFormat,
    additionalHostnames,
    restrictToRegisteredHosts
  ).join("");
};

// `restrictToRegisteredHosts` (default false) preserves the SMS/LINE/Kakao behavior of masking
// every URL onto `hostname` because every URL gets shortened on that host at send time. WhatsApp
// templates only shorten URLs that match a registered shortener with a {{N}} slot, so passing
// `true` makes the function leave URLs on non-registered hosts (e.g. `https://google.com/info`)
// as authored — otherwise the Compose preview misleads authors into thinking those URLs will
// be collapsed onto the picked custom_domain at send time when they will not.
export const tokenizeWithPlaceholderShortUrls = (
  s: string,
  hostname: string,
  userClickTrackingEnabled: boolean,
  personalizeAllUrls: boolean,
  useUnifiedShortLinkFormat = false,
  additionalHostnames: readonly string[] = [],
  restrictToRegisteredHosts = false
) => {
  const placeholderShortUrlBase = `https://${hostname}/XXXXXX`;
  const placeholderPersonalizedShortUrlBase = `https://${hostname}/p/XXXXXXXX`;

  const tokens = tokenizeOnNonLiquidWhitespace(s);
  let urlsShortenedCount = 0;

  // When unified short links is enabled, use the improved URL extraction logic
  if (useUnifiedShortLinkFormat) {
    return tokens.map((token) => {
      // Skip tokens that are pure liquid templates (e.g., {% connected_content https://... %} or {{some_variable}})
      // Don't skip URLs that contain liquid (e.g., https://example.com/{{${first_name}}})
      const isPureLiquid =
        LIQUID_REGEXES.TAG_OR_OBJECT.test(token) && token.replace(LIQUID_REGEXES.TAG_OR_OBJECT, "").trim() === "";
      if (isPureLiquid) {
        return token;
      }

      // Extract URL from this token
      const url = extractHttpUrl(token);
      if (!url || urlsShortenedCount >= sharedConstants.SMS.LIMITS.MAX_LINKS_TO_SHORTEN_IN_BODY) {
        return token;
      }

      // Validate the URL
      const isPersonalized = isPersonalizedHttpUrl(url);
      const isValid = isPersonalized ? isValidPersonalizedHttpUrl(url) : isValidHttpUrl(url);

      if (!isValid) {
        return token;
      }

      const additionalMatch = additionalHostnames.find((h) => h && urlMatchesHost(url, h));
      const onPicked = urlMatchesHost(url, hostname);
      const matchedHostname = additionalMatch ?? (onPicked ? hostname : undefined);

      if (!matchedHostname && restrictToRegisteredHosts) {
        return token;
      }

      // Static URLs on registered shorteners pass through to the BE unchanged.
      // splitWhatsAppTemplateString pulled the {{N}} into a sibling span, so a
      // URL with a real path here is static and must render literally.
      if (restrictToRegisteredHosts && !/^https?:\/\/[^/?#]+\/?$/.test(url)) {
        return token;
      }

      const shortUrl = `https://${matchedHostname ?? hostname}/XXXXXXXX`;

      urlsShortenedCount += 1;
      // Replace the entire URL with the short link placeholder
      return token.replace(url, shortUrl);
    });
  }

  return tokens.map((token) => {
    if (urlsShortenedCount < sharedConstants.SMS.LIMITS.MAX_LINKS_TO_SHORTEN_IN_BODY && isValidHttpUrl(token)) {
      let shortUrl =
        personalizeAllUrls || isPersonalizedHttpUrl(token)
          ? placeholderPersonalizedShortUrlBase
          : placeholderShortUrlBase;
      shortUrl += userClickTrackingEnabled ? PLACEHOLDER_SHORT_URL_USER_ID_PATH : "";

      urlsShortenedCount += 1;
      return shortUrl;
    }
    return token;
  });
};
